import { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Sparkles, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENDERS = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "non_binary", label: "Non-binary" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

const AGE_RANGES = [
  { value: "under_18", label: "Under 18" },
  { value: "18_24", label: "18-24" },
  { value: "25_34", label: "25-34" },
  { value: "35_44", label: "35-44" },
  { value: "45_plus", label: "45+" },
];

const SKIN_TYPES = [
  { value: "oily", label: "Oily" },
  { value: "dry", label: "Dry" },
  { value: "combination", label: "Combination" },
  { value: "sensitive", label: "Sensitive" },
  { value: "normal", label: "Normal" },
];

const SKIN_CONCERNS = [
  "Acne", "Dark Spots", "Oiliness", "Dryness", "Wrinkles",
  "Large Pores", "Redness", "Dullness", "Pigmentation", "Sensitivity",
];

const DESIRED_OUTCOMES = [
  "Clearer skin", "Even skin tone", "Less oiliness", "Better hydration",
  "Anti-aging", "Reduced redness", "Smaller pores", "Brighter complexion",
];

function Pill({ label, selected, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
        selected
          ? "bg-[#5B6BA8] text-white border-[#5B6BA8] shadow-sm"
          : "bg-white text-foreground/70 border-slate-200 hover:border-[#5B6BA8]/40 hover:text-[#5B6BA8]"
      }`}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

function NudgeDialog({ onStay, onLeave }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="nudge-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
            <AlertCircle className="w-5 h-5 text-amber-500" />
          </div>
          <h3 className="text-lg font-bold text-foreground">Are you sure?</h3>
        </div>
        <p className="text-sm text-foreground/70 leading-relaxed mb-6 ml-[52px]">
          Without your skin profile, we can't personalise routines for you. It only takes 30 seconds.
        </p>
        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full px-5"
            onClick={onLeave}
            data-testid="nudge-leave"
          >
            Skip anyway
          </Button>
          <Button
            size="sm"
            className="rounded-full px-5 bg-[#5B6BA8] hover:bg-[#4a5a97]"
            onClick={onStay}
            data-testid="nudge-stay"
          >
            Complete profile
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function OnboardingModal({ onComplete }) {
  const [gender, setGender] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [skinType, setSkinType] = useState("");
  const [concerns, setConcerns] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showNudge, setShowNudge] = useState(false);

  const toggleConcern = (val) => {
    setConcerns(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : prev.length < 3 ? [...prev, val] : prev
    );
  };

  const toggleOutcome = (val) => {
    setOutcomes(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : prev.length < 3 ? [...prev, val] : prev
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/users/profile`, {
        gender,
        age_range: ageRange,
        skin_type: skinType,
        top_concerns: concerns,
        desired_outcomes: outcomes,
        onboarding_complete: true,
      }, { withCredentials: true });
      toast.success("Profile set up! We'll match you to relevant routines.");
      onComplete({ gender, age_range: ageRange, skin_type: skinType, top_concerns: concerns, desired_outcomes: outcomes });
    } catch {
      toast.error("Failed to save. You can update this later in your profile.");
      onComplete(null);
    } finally { setSaving(false); }
  };

  const handleSkipConfirmed = async () => {
    try {
      await axios.patch(`${API}/users/profile`, { onboarding_complete: true, onboarding_skipped: true }, { withCredentials: true });
    } catch { /* silent */ }
    onComplete(null);
  };

  const canSave = skinType && concerns.length > 0;

  return (
    <>
      {showNudge && (
        <NudgeDialog
          onStay={() => setShowNudge(false)}
          onLeave={handleSkipConfirmed}
        />
      )}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="onboarding-modal">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="px-6 pt-6 pb-3 shrink-0">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-9 h-9 rounded-xl bg-[#5B6BA8]/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#5B6BA8]" />
              </div>
              <h2 className="text-xl font-bold text-foreground tracking-tight">Set Up Your Skin Profile</h2>
            </div>
            <p className="text-sm text-foreground/60 ml-[46px]">Takes 30 seconds. Helps us personalise everything for you.</p>
          </div>

          {/* Scrollable content */}
          <div className="px-6 pb-4 overflow-y-auto flex-1 space-y-6">
            {/* Gender */}
            <div>
              <h3 className="text-sm font-bold text-foreground mb-2.5" data-testid="section-gender">Gender</h3>
              <div className="flex flex-wrap gap-2">
                {GENDERS.map(({ value, label }) => (
                  <Pill key={value} label={label} selected={gender === value} onClick={() => setGender(gender === value ? "" : value)} testId={`gender-${value}`} />
                ))}
              </div>
            </div>

            {/* Age */}
            <div>
              <h3 className="text-sm font-bold text-foreground mb-2.5" data-testid="section-age">Age Range</h3>
              <div className="flex flex-wrap gap-2">
                {AGE_RANGES.map(({ value, label }) => (
                  <Pill key={value} label={label} selected={ageRange === value} onClick={() => setAgeRange(ageRange === value ? "" : value)} testId={`age-${value}`} />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-100" />

            {/* Skin Type */}
            <div>
              <h3 className="text-sm font-bold text-foreground mb-1" data-testid="section-skin-type">
                Skin Type <span className="text-red-400 text-xs">*</span>
              </h3>
              <p className="text-xs text-foreground/50 mb-2.5">This is how we match you to routines</p>
              <div className="flex flex-wrap gap-2">
                {SKIN_TYPES.map(({ value, label }) => (
                  <Pill key={value} label={label} selected={skinType === value} onClick={() => setSkinType(skinType === value ? "" : value)} testId={`skin-${value}`} />
                ))}
              </div>
            </div>

            {/* Concerns */}
            <div>
              <h3 className="text-sm font-bold text-foreground mb-1" data-testid="section-concerns">
                Top Concerns <span className="text-red-400 text-xs">*</span>
                <span className="text-foreground/40 font-normal ml-1.5">pick up to 3</span>
              </h3>
              <p className="text-xs text-foreground/50 mb-2.5">What bothers you the most?</p>
              <div className="flex flex-wrap gap-2">
                {SKIN_CONCERNS.map(val => (
                  <Pill key={val} label={val} selected={concerns.includes(val)} onClick={() => toggleConcern(val)} testId={`concern-${val.toLowerCase().replace(/\s+/g, "-")}`} />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-100" />

            {/* Desired Outcomes */}
            <div>
              <h3 className="text-sm font-bold text-foreground mb-1" data-testid="section-outcomes">
                Desired Outcomes
                <span className="text-foreground/40 font-normal ml-1.5">pick up to 3</span>
              </h3>
              <p className="text-xs text-foreground/50 mb-2.5">What does better skin look like for you?</p>
              <div className="flex flex-wrap gap-2">
                {DESIRED_OUTCOMES.map(val => (
                  <Pill key={val} label={val} selected={outcomes.includes(val)} onClick={() => toggleOutcome(val)} testId={`outcome-${val.toLowerCase().replace(/\s+/g, "-")}`} />
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between shrink-0">
            <button
              onClick={() => setShowNudge(true)}
              className="text-xs text-foreground/40 hover:text-foreground/60 transition-colors underline underline-offset-2"
              data-testid="onboarding-skip"
            >
              I'll do this later
            </button>
            <Button
              onClick={handleSave}
              disabled={saving || !canSave}
              className="rounded-full px-8 bg-[#5B6BA8] hover:bg-[#4a5a97] disabled:opacity-40"
              data-testid="onboarding-done"
            >
              {saving ? "Saving..." : "Done"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
